import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useUser } from './UserContext';
import { useNavigate } from 'react-router-dom';

const sanitizeInput = (input) => DOMPurify.sanitize(input);

export default function PrivateFirmenList() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [firmen, setFirmen] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [form, setForm] = useState({
        firmenname: '',
        adresse: '',
        kontaktperson: '',
        email: '',
        telefon: '',
        website: '',
        status: 'offen'
    });
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [focusedField, setFocusedField] = useState(null);

    const handleFocus = (fieldName) => {
        setFocusedField(fieldName);
    };

    const handleBlur = () => {
        setFocusedField(null);
    };

    // Filter firmen based on search term
    const filteredFirmen = firmen.filter(firma =>
        (firma.firmenname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.kontaktperson || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.adresse || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.telefon || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.website || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        if (!user || (user.rolle !== 'ADMIN' && user.rolle !== 'TEACHER')) {
            navigate('/');
            return;
        }
        fetchFirmen();
    }, [user, navigate]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-container')) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const fetchFirmen = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch('https://company-application-platform-backend.onrender.com/api/private-firma', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (res.ok) {
                const data = await res.json();
                setFirmen(data);
                setError(null);
            } else {
                setError('Fehler beim Laden der Firmen: ' + res.status);
            }
        } catch (err) {
            setError('Netzwerkfehler: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: sanitizeInput(value) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!editingId) return;
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(
                `https://company-application-platform-backend.onrender.com/api/private-firma/${editingId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    credentials: 'include',
                    body: JSON.stringify(form)
                }
            );

            if (res.ok) {
                setSuccess('Firma erfolgreich aktualisiert!');
                setForm({
                    firmenname: '',
                    adresse: '',
                    kontaktperson: '',
                    email: '',
                    telefon: '',
                    website: '',
                    status: 'offen'
                });
                setEditingId(null);
                fetchFirmen();
            } else {
                setError('Fehler beim Speichern: ' + res.status);
            }
        } catch (err) {
            setError('Netzwerkfehler: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (firma) => {
        setForm({
            firmenname: firma.firmenname || '',
            adresse: firma.adresse || '',
            kontaktperson: firma.kontaktperson || '',
            email: firma.email || '',
            telefon: firma.telefon || '',
            website: firma.website || '',
            status: firma.status || 'offen'
        });
        setEditingId(firma.id);
        setError(null);
        setSuccess(null);
        setActiveDropdown(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Diese Firma wirklich löschen?')) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`https://company-application-platform-backend.onrender.com/api/private-firma/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            if (res.ok) {
                setSuccess('Firma erfolgreich gelöscht!');
                fetchFirmen();
            } else {
                setError('Fehler beim Löschen: ' + res.status);
            }
        } catch (err) {
            setError('Netzwerkfehler: ' + err.message);
        }
        setActiveDropdown(null);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm({
            firmenname: '',
            adresse: '',
            kontaktperson: '',
            email: '',
            telefon: '',
            website: '',
            status: 'offen'
        });
        setError(null);
        setSuccess(null);
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'offen': return 'status-badge status-offen';
            case 'in Arbeit': return 'status-badge status-in-arbeit';
            case 'versendet': return 'status-badge status-versendet';
            default: return 'status-badge';
        }
    };

    const toggleDropdown = (firmaId, event) => {
        event.stopPropagation();
        setActiveDropdown(activeDropdown === firmaId ? null : firmaId);
    };

    
    return (
        <div>
            <section className="hero-section py-12">
                <div className="container mx-auto px-4">
                    <h1>Private Firmenliste</h1>
                    <p className="lead">
                        Verwalten Sie Ihre privaten Firmeneinträge und deren Status.
                    </p>
                </div>
            </section>

            <section className="page-content-section py-8">
                <div className="container mx-auto px-4">
                    <div className="max-w-7xl mx-auto">
                        {/* Header with Create Button */}
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                Private Firmen ({firmen.length})
                                {searchTerm && ` (${filteredFirmen.length} gefiltert)`}
                            </h2>
                            {user?.rolle === 'ADMIN' && (
                                <button
                                    onClick={() => navigate('/admin/create-private-firma')}
                                    className="btn btn-primary"
                                >
                                    Neue Firma erstellen
                                </button>
                            )}
                        </div>

                        {/* Search Bar */}
                        <div className="mb-6">
                            <div className="search-input-container">
                                <div className="search-input-wrapper">
                                    <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Firma, Kontakt, E-Mail oder Adresse suchen..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="search-input"
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className="search-clear-btn"
                                            type="button"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Messages */}
                        {error && <div className="error-message">{error}</div>}
                        {success && <div className="success-message">{success}</div>}

                        {/* Edit Form - Only show for ADMIN */}
                        {user?.rolle === 'ADMIN' && editingId && (
                            <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                                <h3 className="text-lg font-semibold text-white mb-4">
                                    Firma bearbeiten
                                </h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="input-group">
                                            <label htmlFor="firmenname" className="input-label">
                                                Firmenname *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'firmenname' ? 'focused' : ''} ${form.firmenname ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                                <input
                                                    id="firmenname"
                                                    name="firmenname"
                                                    type="text"
                                                    required
                                                    value={form.firmenname}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('firmenname')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="Firmenname"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="adresse" className="input-label">
                                                Adresse *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'adresse' ? 'focused' : ''} ${form.adresse ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <input
                                                    id="adresse"
                                                    name="adresse"
                                                    type="text"
                                                    required
                                                    value={form.adresse}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('adresse')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="Musterstraße 123, 1234 Stadt"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="kontaktperson" className="input-label">
                                                Kontaktperson *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'kontaktperson' ? 'focused' : ''} ${form.kontaktperson ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                <input
                                                    id="kontaktperson"
                                                    name="kontaktperson"
                                                    type="text"
                                                    required
                                                    value={form.kontaktperson}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('kontaktperson')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="Max Mustermann"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="email" className="input-label">
                                                E-Mail *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'email' ? 'focused' : ''} ${form.email ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    required
                                                    value={form.email}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('email')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="kontakt@firma.ch"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="telefon" className="input-label">
                                                Telefon *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'telefon' ? 'focused' : ''} ${form.telefon ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                <input
                                                    id="telefon"
                                                    name="telefon"
                                                    type="tel"
                                                    required
                                                    value={form.telefon}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('telefon')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="+41 79 123 45 67"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="website" className="input-label">
                                                Website
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'website' ? 'focused' : ''} ${form.website ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <input
                                                    id="website"
                                                    name="website"
                                                    type="url"
                                                    value={form.website}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('website')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="https://www.firma.ch"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="status" className="input-label">
                                                Status *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'status' ? 'focused' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                </svg>
                                                <select id="status" name="status" value={form.status} onChange={handleChange} onFocus={() => handleFocus('status')} onBlur={handleBlur} className="form-input status-badge" disabled={loading}>
                                                    <option value="offen">Offen</option>
                                                    <option value="in Arbeit">In Arbeit</option>
                                                    <option value="versendet">Versendet</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='input-b'>
                                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                            <div className="flex gap-3">
                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className="btn btn-primary"
                                                >
                                                    {loading ? 'Speichern...' : 'Aktualisieren'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEdit}
                                                    disabled={loading}
                                                    className="btn btn-outline"
                                                >
                                                    Abbrechen
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Firmen Table */}
                        {loading && firmen.length === 0 ? (
                            <div className="flex justify-center items-center p-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                            </div>
                        ) : (searchTerm ? filteredFirmen : firmen).length === 0 ? (
                            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {searchTerm ? 'Keine passenden Firmen gefunden' : 'Keine privaten Firmen vorhanden'}
                                </h3>
                                <p className="text-gray-500">
                                    {searchTerm
                                        ? 'Versuchen Sie einen anderen Suchbegriff'
                                        : (user?.rolle === 'ADMIN' ? 'Erstellen Sie Ihre erste private Firma.' : 'Es wurden noch keine privaten Firmen erstellt.')}
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow-lg">
                                <div>
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Firmenname</th>
                                                <th>Adresse</th>
                                                <th>Kontaktperson</th>
                                                <th>E-Mail</th>
                                                <th>Telefon</th>
                                                <th>Website</th>
                                                <th>Status</th>
                                                {user?.rolle === 'ADMIN' && <th className="text-center">Aktionen</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(searchTerm ? filteredFirmen : firmen).map((f) => (
                                                <tr key={f.id}>
                                                    <td className="font-medium text-gray-900">{f.firmenname}</td>
                                                    <td className="text-gray-700">{f.adresse}</td>
                                                    <td className="text-gray-700">{f.kontaktperson || '-'}</td>
                                                    <td className="text-gray-700">
                                                        <a href={`mailto:${f.email}`} className="text-blue-600 hover:text-blue-800">
                                                            {f.email}
                                                        </a>
                                                    </td>
                                                    <td className="text-gray-700">
                                                        <a href={`tel:${f.telefon}`} className="text-blue-600 hover:text-blue-800">
                                                            {f.telefon}
                                                        </a>
                                                    </td>
                                                    <td className="text-gray-700">
                                                        {f.website ? (
                                                            <a href={f.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                                                Website
                                                            </a>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
                                                        <span className={getStatusBadgeClass(f.status)}>
                                                            {f.status}
                                                        </span>
                                                    </td>
                                                    {user?.rolle === 'ADMIN' && (
                                                        <td className="text-center">
                                                            <div className="dropdown-container">
                                                                <button
                                                                    onClick={(e) => toggleDropdown(f.id, e)}
                                                                    className="dropdown-trigger"
                                                                    disabled={loading}
                                                                >
                                                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                                    </svg>
                                                                </button>

                                                                {activeDropdown === f.id && (
                                                                    <div className="dropdown-menu">
                                                                        <button
                                                                            onClick={() => handleEdit(f)}
                                                                            className="dropdown-item dropdown-item-edit"
                                                                            disabled={loading}
                                                                        >
                                                                            Bearbeiten
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleDelete(f.id)}
                                                                            className="dropdown-item dropdown-item-delete"
                                                                            disabled={loading}
                                                                        >
                                                                            Löschen
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}